<template>
    <base-modal size="lg" hide-header hide-footer :allow-overflow="true" @close="$emit('close')">
        <div class="col-12 nopads">
            <div class="col-sm-12 editor-form">
                <h2 class="editor-header">{{ $t('work_assignment.edit_multiple') }}</h2>
                <div class="info-box mt-4 col-12 my-2">
                    <font-awesome-icon icon="info-circle" class="info-circle" />
                    <span>{{ $t('work_assignment.bulk_edit_hint') }}</span>
                </div>


                <div class="row mt-2">
                    <div class="col-12 col-sm-6">
                        <span class="span-title">{{ $t('work_assignment.assignee_company') }}</span>
                        <select v-model="editCompany" class="form-select form-select-sm"
                                :disabled="!companyOptions || companyOptions.length < 1"
                                @change="onEditCompanyChange">
                            <option :value="null">{{ $t('work_assignment.no_change') }}</option>
                            <option :value=-1>{{ $t('work_assignment.no_responsible_company') }}</option>
                            <option v-for="c in companyOptions" :key="c.value" :value="c.value">{{ c.name }}</option>
                        </select>
                    </div>
                    <div class="col-12 col-sm-6">
                        <span class="span-title">{{ $t('work_assignment.select_assignee') }}</span>
                        <select v-model="editPerson" class="form-select form-select-sm"
                                @change="onEditUserChange">
                            <option :value="null">{{ $t('work_assignment.no_change') }}</option>
                            <option :value=-1>{{ $t('work_assignment.no_responsible_person') }}</option>
                            <option v-for="user in editUsers" :key="user.id" :value="user.id">{{ user.last_name }}, {{ user.first_name }}</option>
                        </select>
                    </div>
                    <div class="col-12 col-sm-6">
                        <span class="span-title">{{ $t('orders.contract') }}</span>
                        <multiselect v-model="editContract" :options="contracts" label="name" track-by="id"
                                     :placeholder="$t('work_assignment.no_change')" :allow-empty="true" :show-labels="false"
                                     @update:modelValue="onEditContractChange" />
                    </div>
                    <div class="col-12 col-sm-6">
                        <span class="span-title">{{ $t('observations.orders') }}</span>
                        <select v-model="editOrder" class="form-select form-select-sm"
                                :disabled="!editContract || editOrders.length === 0">
                            <option :value="null">{{ $t('observations.select_order') }}</option>
                            <option v-for="order in editOrders" :key="order.id" :value="order.id">{{ order.id }} - {{ getOrderAndExecutorStr(order) }}</option>
                        </select>
                    </div>
                    <div class="col-12 col-sm-6">
                        <span class="span-title">{{ $t('work_assignment.select_status') }}</span>
                        <select v-model="editStatus" class="form-select form-select-sm">
                            <option :value="null">{{ $t('work_assignment.no_change') }}</option>
                            <option v-for="option in getStatus(false)" :key="option.status" :value="option.status">{{ option.key }}</option>
                        </select>
                    </div>
                    <div class="col-12 col-sm-6">
                        <span class="span-title">{{ $t('observation_list.archived') }}</span>
                        <select v-model="editArchived" class="form-select form-select-sm">
                            <option :value="null">{{ $t('work_assignment.no_change') }}</option>
                            <option :value="true">{{ $t('common.yes') }}</option>
                            <option :value="false">{{ $t('common.no') }}</option>
                        </select>
                    </div>
                </div>

                <!-- Validation + buttons -->
                <span v-if="validationError" class="text-danger d-block mt-2">{{ validationError }}</span>
                <div class="col-sm-12 text-end mt-3 pt-2">
                    <button class="btn btn-secondary form-button" @click.stop="$emit('close')">
                        {{ $t('common.cancel') }}
                    </button>
                    <button :disabled="loading" class="btn btn-success form-button" @click.stop="submitBulkUpdate()">
                        {{ $t('common.save') }}
                    </button>
                </div>
            </div>
            <div v-if="loading" id="loader" class="spinner" />
        </div>
    </base-modal>
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import {timeUtils} from '../mixins/TimeUtils'
import {workAssignmentHelper} from '../mixins/WorkAssignmentMixin'
import {orderMixin} from "../mixins/OrderMixin.js";
import BaseModal from '../BaseModal'
import {inject} from "vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";

export default {
    name: 'WorkAssignmentBulkEditor',
    components: {FontAwesomeIcon, BaseModal },
    mixins: [restApi, timeUtils, workAssignmentHelper, orderMixin],
    props: {
        assignments: { type: Array, default: () => [] },
        isObserver: { type: Boolean, default: false }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    emits: ['close', 'closeAndUpdate'],
    data() {
        return {
            loading: 0,
            validationError: null,
            // Edit tab fields — null = "no change"
            editStatus: null,
            editArchived: null,
            editCompany: null,   // company ID (integer)
            editPerson: null,
            editContract: null,  // contract object
            editOrder: null,
            // Dropdown lists
            companyOptions: [],  // [{name, value}] — populated from contractCompanyUrl
            editUsers: [],
            contracts: [],
            editOrders: [],
        }
    },
    mounted() {
        this.fetchContracts()
        this.fetchCompanies()
        this.fetchUsers()
    },
    methods: {
        fetchCompanies() {
            this.companyOptions = []
            this.loading++
            this.restFetch(this.contractorUrl, response => {
                if (response && response.data) {
                    response.data.forEach(company => {
                        this.companyOptions.push({ name: company.name, value: company.id })
                    })
                }
                this.loading--
            })
        },
        fetchUsers() {
            this.loading++
            this.restFetchParams(this.userCompanyUrl, {active: 1}, resp => {
                this.editUsers = resp?.data ?? []
                this.loading--
            })
        },
        fetchContracts() {
            this.loading++
            this.restFetch(this.contractOpenUrl, response => {
                this.contracts = response?.data ?? []
                this.loading--
            })
        },
        fetchEditOrders(contractId) {
            this.loading++
            this.restFetchParams(this.orderUrl, { contract: contractId, open: 1 }, resp => {
                this.editOrders = resp?.data ?? []
                this.loading--
            })
        },
        onEditCompanyChange() {
            this.editPerson = -1
        },
        onEditUserChange() {
            if (this.editUser !== null) {
                this.editCompany = -1
                this.status = this.ASSIGNED
            } else {
                this.status = this.OPEN
            }
        },
        onEditContractChange(contract) {
            this.editContract = contract
            this.editOrder = null
            this.editOrders = []
            this.editCompany = -1
            this.editPerson = -1
            this.editUsers = []
            if (contract) {
                this.fetchEditOrders(contract.id)
            }
        },
        buildEditPayload() {
            const payload = { assignments: this.assignments.map(a => a.id) }
            if (this.editStatus !== null) payload.status = this.editStatus
            if (this.editArchived !== null) payload.archived = this.editArchived
            if (this.editCompany !== null) payload.assigned_company = this.editCompany
            if (this.editPerson !== null) payload.assigned_to = this.editPerson
            if (this.editContract !== null) payload.contract = this.editContract.id
            if (this.editOrder !== null) payload.work_order = this.editOrder
            return payload
        },
        submitBulkUpdate() {
            if (this.editStatus === null && this.editArchived === null && this.editCompany === null &&
                this.editPerson === null && this.editContract === null && this.editOrder === null) {
                this.validationError = this.$t('work_assignment.bulk_edit_no_fields')
                return
            }
            this.validationError = null
            this.loading++
            this.restAdd(this.workAssignmentsUrl, this.buildEditPayload(), this.onSuccess, this.onFail)
        },
        onSuccess() {
            this.loading--
            this.$emit('closeAndUpdate')
        },
        onFail() {
            this.loading--
            this.showAlert(this.$t('common.alert_update_failed'))
        }
    }
}
</script>

<style>
@import "../../styles/modal.css";
</style>
